import React, { ReactElement } from 'react';
import { Alert, AlertDescription } from '@zapier/design-system';
import {
  ApiKeys,
  HostedFieldAdditionalData,
  HostedFieldToken,
  HostedFieldType,
  HostedFieldValue,
  PaymentProcessor,
} from '@zapier/payment/src/types';
import { usePaymentProcessor } from './usePaymentProcessor';

type Props = {
  /** Payment processor API keys */
  apiKeys: ApiKeys;
  /** Payment processor to use */
  paymentProcessor: PaymentProcessor;
  children: (
    createToken: (
      additionalData: HostedFieldAdditionalData
    ) => Promise<HostedFieldToken>
  ) => ReactElement;
  /** Call when field changes */
  onChange: (name: HostedFieldType, value: HostedFieldValue) => void;
};

const HostedFieldsProvider: React.FC<Props> = props => {
  const {
    paymentProcessor,
    apiKeys: { [paymentProcessor]: apiKey },
    onChange,
  } = props;

  const [processor, error] = usePaymentProcessor(
    paymentProcessor,
    apiKey,
    onChange
  );

  const createToken = (
    additionalData: HostedFieldAdditionalData
  ): Promise<HostedFieldToken> => {
    if (!processor) throw new Error('Payment processor is not ready.');
    return processor.createToken(additionalData);
  };

  if (error) {
    return (
      <Alert title={error.title} type="error">
        <AlertDescription>{error.description}</AlertDescription>
      </Alert>
    );
  }

  return props.children(createToken);
};

export default HostedFieldsProvider;
